import React from 'react';

// ───────── Shared shell ─────────
const ModalShell = ({ open, onClose, width = 720, children }) => {
  if (!open) return null;
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(20, 40, 20, 0.55)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="room-themed-panel"
        style={{
          width,
          maxWidth: '94vw',
          maxHeight: '92vh',
          overflowY: 'auto',
          border: '2px solid',
          boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
          padding: '22px 28px',
          fontFamily: 'Arial, Helvetica, sans-serif',
        }}
      >
        {children}
      </div>
    </div>
  );
};

const CloseBtn = ({ onClose }) => (
  <div style={{ textAlign: 'center', marginTop: 14 }}>
    <button
      onClick={onClose}
      style={{
        backgroundColor: '#1c3a1c',
        color: '#ecfcd9',
        border: '1px solid #2d6b1e',
        padding: '4px 18px',
        fontWeight: 'bold',
        fontSize: '13px',
        cursor: 'pointer',
      }}
    >
      Close
    </button>
  </div>
);

const Section = ({ title, children }) => (
  <div
    className="room-themed-bg room-themed-border"
    style={{
      border: '1px solid',
      padding: '12px 16px',
      marginTop: 10,
    }}
  >
    {title && (
      <div style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 8 }}>
        {title}
      </div>
    )}
    {children}
  </div>
);

const Cmd = ({ name, desc, onUse }) => (
  <div style={{ marginBottom: 8 }}>
    <div
      style={{ fontWeight: 'bold', cursor: onUse ? 'pointer' : 'default', color: '#1a4a10' }}
      onClick={onUse}
      title={onUse ? 'Click to insert into input' : ''}
    >
      {name}
    </div>
    <div style={{ marginLeft: 18, fontSize: 12, lineHeight: 1.5 }}>{desc}</div>
  </div>
);

// ───────── Tutorial Modal ─────────
export const TutorialModal = ({ open, onClose, onInsertCommand }) => {
  const insert = (cmd) => {
    if (onInsertCommand) onInsertCommand(cmd);
    onClose();
  };
  return (
    <ModalShell open={open} onClose={onClose} width={780}>
      {/* Msg #968 — Combat, Stats & Adventurer Ranks moved to TOP of tutorial */}
      <Section title={<span style={{ fontSize: 16 }}>Combat, Stats &amp; Adventurer Ranks</span>}>
        <div style={{
          border: '1px solid #2d6b1e', background: '#f7fbe9',
          padding: '8px 12px', fontSize: 12, color: '#1c3f1c', lineHeight: 1.55,
        }}>
          <b>Your three stats</b> —
          <ul style={{ margin: '4px 0 8px 20px', padding: 0 }}>
            <li><b>Attack</b> — damage dealt when your strike lands. Novice = <b>+1</b>, +1 per rank (up to Champion = <b>+5</b>).</li>
            <li><b>Health (HP)</b> — how many hits you can take before you fall. Everyone starts at 10 HP; ranks up by <b>+2 HP</b> per rank.</li>
            <li><b>Defense</b> — SUBTRACTED from an attacker&apos;s dice roll. Novice = <b>+1</b>, Champion = <b>+4</b>.</li>
          </ul>
          <b>Dice combat</b> — <code>/attack {'{npc}'}</code> rolls <b>1d20</b>. The target&apos;s defense is subtracted.
          A roll of <b>10 or less always MISSES</b>. On a roll of <b>11 or more</b> the strike lands (net roll ≥ 11 after defense) for <b>1 HP</b> damage.
          Fatal blows drop the target to 0 HP and post a death line; they revive after a short cooldown.
          <br /><br />
          <b>Magic Points (MP)</b> — everyone starts with <b>5 MP</b>, regenerating <b>+1 per minute</b>. Priests, healers, and mages spend MP to cast:
          <ul style={{ margin: '4px 0 8px 20px', padding: 0 }}>
            <li><code>/magic {'{npc}'}</code> — <b>1 MP</b> and the caster takes <b>1 HP recoil</b>. Roll <b>1d20</b>; a roll of <b>12 or more</b> hits for <b>2 HP</b> (magic pierces defense). 11 or less misses. Recoil effect stops once the caster reaches the rank of <b>Veteran</b>.</li>
            <li><code>/heal</code> — <b>1 MP</b>. Restores <b>+2 HP</b> to yourself. No dice roll.</li>
            <li><code>/pray Anu</code> or <code>/pray Danu</code> — free, but the priestess must be in your room. Gradual <b>+1 HP every 30 s over 90 s</b> (3 ticks).</li>
          </ul>
          <br /><br />
          <b>Adventurer Ranks</b> (progress by killing monsters — one rank every 10 kills):
          <table style={{ marginTop: 4, fontSize: 11, borderCollapse: 'collapse' }}>
            <tbody>
              <tr style={{ background: '#dfeecb' }}>
                <td style={{ padding: '2px 6px', border: '1px solid #2d6b1e' }}><b>Rank</b></td>
                <td style={{ padding: '2px 6px', border: '1px solid #2d6b1e' }}><b>Kills needed</b></td>
                <td style={{ padding: '2px 6px', border: '1px solid #2d6b1e' }}><b>Atk</b></td>
                <td style={{ padding: '2px 6px', border: '1px solid #2d6b1e' }}><b>Def</b></td>
                <td style={{ padding: '2px 6px', border: '1px solid #2d6b1e' }}><b>HP</b></td>
              </tr>
              {[
                ['Novice',    '—',   1, 1, 10],
                ['Adventurer', '10', 2, 2, 12],
                ['Veteran',    '20', 3, 3, 14],
                ['Hero',       '30', 4, 3, 16],
                ['Champion',   '50', 5, 4, 18],
              ].map((r) => (
                <tr key={r[0]}>
                  {r.map((c, i) => (
                    <td key={i} style={{ padding: '2px 6px', border: '1px solid #2d6b1e' }}>{c}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <br />
          <b>Fainting &amp; Healing</b> — if your HP hits <b>0</b> you FAINT.
          Your name is shown as <code>YourName_(fainted)</code> in chat and you cannot deal damage.
          To recover, either
          <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
            <li>Find <b>Goddess_Sophetia</b> in the same room and type <code>/bless</code> — full heal.</li>
            <li>Enter the <b>Church</b> — you regain <b>+2 HP</b> per one of your posts while in the Church.</li>
          </ul>
          <br />
          <b>Affection &amp; Dislike</b> — Positive lines toward an NPC (compliments, gifts, careful RP)
          earn <b>💚 +1</b> every 5 positive turns per day.  Aggression, insults, or unwanted grabs earn
          <b> 💢 +1</b> each. Reaching <b>💚 5</b> unlocks pet-names and gifts; reaching <b>💢 10</b>
          provokes the NPC to attack you unprompted. Use <code>/like {'{npc}'}</code> or
          <code> /dislike {'{npc}'}</code> to nudge manually.
        </div>
      </Section>

      <Section title={<span style={{ fontSize: 16 }}>Text formating for messages</span>}>
        <div style={{ fontSize: 13, lineHeight: 1.55 }}>
          You can put <b>bold</b>, <i>italic</i> or <u>underlined</u> text in messages by encasing
          the applicable sections of your text with either the <b>&lt;B&gt; &lt;/B&gt;</b>,{' '}
          <b>&lt;I&gt; &lt;/I&gt;</b> or <b>&lt;U&gt; &lt;/U&gt;</b> tags.
          <br />
          For example, <code>&lt;B&gt;this text&lt;/B&gt;</code> will produce <b>this text</b>.
          <br />
          <br />
          To create a hyperlink (for e-mail or URL) in your message, simply type the corresponding
          address without any tag. The hyperlink will be created automatically.
        </div>
      </Section>

      <Section title={<span style={{ fontSize: 16 }}>Commands</span>}>
        <div style={{ textAlign: 'center', fontSize: 12, fontStyle: 'italic', marginBottom: 8 }}>
          {'{}'} represents a required setting, [] for an optional one. Click on a command to use
          it.
        </div>
        <Cmd name="/!" desc="Recall the previous text typed (command or message)." onUse={() => insert('/!')} />
        <Cmd
          name="/alias {name|off}   /nick {name|off}"
          desc="Set your alias name to this value or turns it off if 'off' is specified. Useful for role-playing NPCs."
          onUse={() => insert('/alias ')}
        />
        <Cmd
          name="/announce {message}"
          desc="Allow the administrator to send an announcement to all users in all chat rooms."
          onUse={() => insert('/announce ')}
        />
        <Cmd
          name="/ban [*] {user}"
          desc='Allow the moderator/admin to "banish" a user. Admins can also use "*" to banish them from the chat as a whole.'
          onUse={() => insert('/ban ')}
        />
        <Cmd name="/clear" desc="Clear the messages frame and show only the last 5 messages." onUse={() => insert('/clear')} />
        <Cmd
          name="/color {hex}   /co {hex}"
          desc="Set your text color to this web-style color code. FF0000 = Red, 00FF00 = Green, 0000FF = Blue."
          onUse={() => insert('/color ')}
        />
        <Cmd
          name="/ignore [-] [user[,user...]]"
          desc='Avoid displaying messages from a user. With no option, pops up a list of all ignored nicks.'
          onUse={() => insert('/ignore ')}
        />
        <Cmd
          name="/invite {user[,user...]}"
          desc="Invite other chat users to join the room you are in. Required to join private rooms."
          onUse={() => insert('/invite ')}
        />
        <Cmd
          name="/join {room} [password]   /go {room} [password]"
          desc="Join another room. Room names cannot contain spaces."
          onUse={() => insert('/join ')}
        />
        <Cmd
          name="/private {room} {password}"
          desc="Create a private room with a password (registered users only)."
          onUse={() => insert('/private ')}
        />
        <Cmd name="/kick {user}" desc="Kick a user out of the room. Moderator only." onUse={() => insert('/kick ')} />
        <Cmd
          name=":{message}   /me {message}   /she {message}   /he {message}"
          desc="Describe an action you are doing."
          onUse={() => insert('/me ')}
        />
        <Cmd
          name="/msg {user} {msg}   /to {user} {msg}   /whisper {user} {msg}   /wh {user} {msg}"
          desc="Send a private message to a user in the same room."
          onUse={() => insert('/whisper ')}
        />
        <Cmd name="/notify" desc="Toggle notifications of user entrance/exit for the current room." onUse={() => insert('/notify')} />
        <Cmd name="/profile" desc="Popup a window for editing your profile." onUse={() => insert('/profile')} />
        <Cmd name="/promote {user}" desc="Promote a registered user to room moderator." onUse={() => insert('/promote ')} />
        <Cmd name="/demote [*] {user}" desc="Administrator-only: demote a moderator back to user." onUse={() => insert('/demote ')} />
        <Cmd
          name="/quit [msg]   /exit [msg]   /bye [msg]"
          desc="Leave the chat after displaying an optional message."
          onUse={() => insert('/quit')}
        />
        <Cmd
          name="/refresh [n]"
          desc="Modify message/userlist refresh delay (in seconds). With no number, toggles between off and 10s."
          onUse={() => insert('/refresh ')}
        />
        <Cmd
          name="/roll {NdD+X}"
          desc="Roll a die in the chat window. N = number of rolls, D = number of sides, X = +/- modifier."
          onUse={() => insert('/roll 1d20')}
        />
        <Cmd name="/roll passfail" desc="Flip a Pass/Fail coin." onUse={() => insert('/roll passfail')} />

        <Cmd
          name="/attack {npc}"
          desc="RPG combat (1d20). Roll ≤10 = miss; roll ≥11 with net (roll − defense) ≥ 11 = 1 HP hit. Fatal blow drops to 0 HP; revives after cooldown. E.g. /attack Golem"
          onUse={() => insert('/attack ')}
        />
        <Cmd
          name="/combat {npc}"
          desc="Summon Referee_R to oversee a formal duel. He walks into the room, sets the rules, and stays as long as combat continues. Then use /attack {npc} to fight."
          onUse={() => insert('/combat ')}
        />
        <Cmd
          name="/heal"
          desc="Cast Heal on yourself — costs 1 MP, restores +2 HP with no dice roll. Priest/healer spell."
          onUse={() => insert('/heal')}
        />
        <Cmd
          name="/magic {npc}"
          desc="Cast a magic strike on an NPC — costs 1 MP + 1 HP self-recoil (recoil is waived at Veteran rank and above). Roll 1d20; 12+ hits for 2 HP (magic pierces defense). E.g. /magic Werewolf"
          onUse={() => insert('/magic ')}
        />
        <Cmd
          name="/pray {Anu|Danu}"
          desc="Ask Priestess Anu or Danu to pray for you — must be in your room. +1 HP every 30 s over 90 s (3 ticks). Free."
          onUse={() => insert('/pray ')}
        />
        <Cmd
          name="/arouse {npc}"
          desc="Bias a Champion NPC toward initiating Erotic RP with you for the next 30 min. Champion NPCs still require Champion-rank / Mod / Admin status to actually engage."
          onUse={() => insert('/arouse ')}
        />
        <Cmd
          name="/orgy [prompt]"
          desc="Start a chained NPC scene. Auto-locates any private room with 2+ NPCs. If you're not in one, you'll be moved."
          onUse={() => insert('/orgy ')}
        />
        <Cmd
          name="/nowplaying {title}"
          desc="Set the shared 🎬 Now Watching marquee for this room (great for Movie_Night). Empty title clears it. Also relays to Discord in linked rooms."
          onUse={() => insert('/nowplaying ')}
        />
        <Cmd
          name="/badending {npc}    /bad-ending {npc}"
          desc="Trigger a monster/NPC to detain, restrain, seduce and fondle you — culminating in a private-room invite. E.g. /badending Werewolf"
          onUse={() => insert('/badending ')}
        />
        <Cmd
          name="/like {npc}    /dislike {npc}"
          desc="Manually give an NPC 💚 affection or 💢 dislike. Reaching +5 hearts unlocks pet-names / gifts; +10 dislikes provokes an attack."
          onUse={() => insert('/like ')}
        />
        <Cmd
          name="/recall {npc} [room]"
          desc="Msg #975 — ANYONE can now bring an NPC/monster into a specified room (defaults to your current room). Ex: /recall Aurelia MainHall"
          onUse={() => insert('/recall ')}
        />
        <Cmd
          name="/lockin {npc}   /lockin off"
          desc="Msg #975 — Anyone can lock an NPC in this room to reply ONLY to you until you release. Auto-expires in 20 minutes. Ex: /lockin Ryder"
          onUse={() => insert('/lockin ')}
        />
        <Cmd
          name="/terminate {npc}"
          desc="Msg #975 — Send an NPC in this room away to a random other room. Ex: /terminate Ogre"
          onUse={() => insert('/terminate ')}
        />
        <Cmd
          name="/orgy [prompt]"
          desc="Msg #976 — In a PRIVATE room with 2+ NPCs, start a chained scene where each NPC takes turns replying to you and each other. Uncensored (Dolphin-Mixtral). Requires /askprivate first. Ex: /orgy the fire crackles low"
          onUse={() => insert('/orgy ')}
        />
        <Cmd
          name="/dismiss {npc}   (admin only)"
          desc="Kick an NPC/monster out of the current room."
          onUse={() => insert('/dismiss ')}
        />
        <Cmd
          name="/spawn {monster}   (admin only)"
          desc="Spawn a monster/NPC into the current room."
          onUse={() => insert('/spawn ')}
        />
        <Cmd
          name="/leaderboard   /top"
          desc="Show the top 10 combatants of Mandala, ranked by damage dealt."
          onUse={() => insert('/leaderboard')}
        />
        <Cmd
          name="/show [n]   /last [n]"
          desc="Reload the messages frame and display the [n] latest messages, minimum/default is 5."
          onUse={() => insert('/show ')}
        />
        <Cmd name="/timestamp" desc="Show/Hide time display before messages." onUse={() => insert('/timestamp')} />
        <Cmd name="/whois {user}" desc="Show information about a specified user." onUse={() => insert('/whois ')} />
        <Cmd
          name="/describe {room description}"
          desc="Attach a description (or welcome message) to your room. Works for user-created and private rooms only."
          onUse={() => insert('/describe ')}
        />
      </Section>

      <CloseBtn onClose={onClose} />
    </ModalShell>
  );
};

// ───────── Site Rules Modal ─────────
export const SiteRulesModal = ({ open, onClose }) => {
  return (
    <ModalShell open={open} onClose={onClose} width={820}>
      <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 16, marginBottom: 12 }}>
        MANDALA AUP SITE RULES / USER AGREEMENT
      </div>

      <div style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
        <b>Acceptable Use Policy</b>
        {'\n\n'}
        Users agree to this document by using the services provided by Mandala.
        {'\n\n'}
        <b>1.</b> As no AUP can cover every possible scenario that may come up, individuals will be
        handled on a case-by-case basis. Transgressions that are out of step with the spirit of our
        rules — but not specifically referred to in the AUP — are subject to the staff&apos;s
        discretion. Abuse of this discretion may be reported to the Ombudsmen. Any effort to defeat
        or circumvent the rules, or to falsify a report in an attempt to manipulate them against
        another user, constitutes a violation of this document.
        {'\n\n'}
        <b>2.</b> Users are to keep profanity to a minimum, or between characters and not players.
        Casual swearing is acceptable in this chat room. Harsher language is also acceptable, to a
        point that is not excessive, at the staff&apos;s discretion. Flooding the chat with
        swearing for the sake of it is a breach of this rule. Room and character names may not
        contain swears, curses, slurs, or sexually explicit words or phrases. These standards also
        apply to all links posted within the chat.
        {'\n\n'}
        <b>3.</b> Profiles may contain anything except underage sexual acts.
        {'\n\n'}
        <b>4. Real Life</b> — Respect each other&apos;s privacy Out Of Character (OOC). Users are
        prohibited from engaging in behavior that could jeopardize the safety of, violate the
        privacy of, cause harm, or threaten other users in real life. It is also forbidden for a
        user to spread personal, private information about (either true or libelous), or make
        real-life (RL) threats against another user. Repeatedly seeking personal, private
        information about another user that they don&apos;t want known will be considered
        harassment and treated as such. Advocating real-life hate campaigns is strictly prohibited.
        Punishment for these transgressions will be given according to the severity of the
        perceived threat, the number of offenses the aggressor(s) have made before, and whether or
        not the affected party wishes to make a complaint.
        {'\n\n'}
        <b>5.</b> Cyber sex or &apos;cybering&apos; / &apos;ERP&apos; is STRICTLY FORBIDDEN in
        OOC_Room, and the MainHall, or member-created rooms that do not wish to have it — they
        must have SFW in the room&apos;s description. Any other room is free game.
        {'\n\n'}
        <b>6.</b> Public room tags are allowed.
        {'\n\n'}
        <b>7.</b> Attempted port scans, probes, uses of known network security tools, or otherwise
        seeking security flaws in Mandala or any of the infrastructures supporting it without
        express permission from the System Operator will make that user subject to permanent
        banning at the discretion of the System Operator. ISP and Federal officials may also be
        notified. Likewise, user/s who attempt what may be construed as a Denial-of-Service attack
        on the network, or any attempt to disrupt the service through any security flaw or
        otherwise, will be subject to permanent banning. ISP and Federal officials may also be
        notified. Threats of hacking or other actions that compromise the security and/or daily
        operations of Mandala, whether in chat, on forums, or in messengers, will be dealt with as
        stated above.
        {'\n\n'}
        <b>8.</b> Users shall not harass other users. Harassment in this chatroom includes any
        unwanted or bothersome attention, particularly when the harasser has been asked to leave
        him or her alone. If harassing behavior persists to the point where the /ignore function
        is no longer helpful, the harasser is in violation of this rule and may be dealt with by
        the staff. Out of Character harassment — and especially sexual harassment — is not
        tolerated and will be punished accordingly.
        {'\n\n'}
        <b>9.</b> Any user who uses Mandala for the purposes of spamming will be subject to
        kicking or any other punishment deemed appropriate by the administrative staff. Spam is
        defined as the repetitive posting of relatively nonsensical or meaningless letters,
        numbers, text, or URLs. URL spam can consist of advertising other roleplay sites or
        repeatedly dropping links, especially if you room-hop to do it. Please also refrain from
        randomly capitalizing letters in your posts, using font colors (like white text on a white
        background or bright green) that are difficult to read.
        {'\n\n'}
        <b>10.</b> Default IC rooms are not meant for OOC discussions. OOC for the purpose of
        clarification regarding an ongoing roleplay (such as asking about or responding to an
        inquiry about an IC currently in the room) is acceptable when done sparingly. For any more
        extensive out-of-character discussions, please use the whisper function.
        {'\n\n'}
        The Default IC rooms are as follows: <b>Pub, Café, Forest, Desert, Garden, Arena,
        EastTower, WestTower, Mandala, Far Planes, Nexus_City, The_Barn, Sky_City, Night_Club,
        Strip_Club, Inn_Brothel</b>.
        {'\n\n'}
        <b>11. Terms of Service</b>
        {'\n'}
        <i>The Services are Available &quot;AS-IS and At-Your-Own-Risk&quot;</i>
        {'\n\n'}
        Your access to and use of the Services or any Content are at your own risk. You understand
        and agree that speaking to potentially anonymous people you may or may not know and may or
        may not be able to trust, on antiquated technology from before the dawn of the modern
        internet, moderated mostly by your peer users who have volunteered for such a role, is an
        activity that carries considerable inherent risks, and that you understand, accept, and
        have specifically and deliberately sought to engage in such an environment despite or
        because of those risks.
        {'\n\n'}
        You understand and agree that the Services are provided to you on an &quot;AS IS&quot; and
        &quot;AS AVAILABLE&quot; basis. The &quot;Host Entities&quot; refers to Mandala, its
        parents, affiliates, related companies, officers, directors, employees, agents,
        representatives, partners, and licensors. Without limiting the foregoing, to the maximum
        extent permitted under applicable law, THE HOST ENTITIES DISCLAIM ALL WARRANTIES AND
        CONDITIONS, WHETHER EXPRESS OR IMPLIED, OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
        PURPOSE, OR NON-INFRINGEMENT.
        {'\n\n'}
        The Host Entities make no warranty or representation and disclaim all responsibility and
        liability for: (i) the completeness, accuracy, availability, timeliness, security or
        reliability of the Services or any Content; (ii) any harm to your computer system, loss of
        data, or other harm that results from your access to or use of the Services or any
        Content; (iii) the deletion of, or the failure to store or to transmit, any Content and
        other communications maintained by the Services; and (iv) whether the Services will meet
        your requirements or be available on an uninterrupted, secure, or error-free basis. No
        advice or information, whether oral or written, obtained from the Host Entities or
        through the Services, will create any warranty or representation not expressly made
        herein.
        {'\n\n'}
        <b>Limitation of Liability</b>
        {'\n\n'}
        TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE HOST ENTITIES SHALL NOT BE LIABLE
        FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR PUNITIVE DAMAGES, OR ANY LOSS OF
        PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE,
        GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM
        {'\n'}
        (i) YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SERVICES; (ii) ANY CONDUCT
        OR CONTENT OF ANY THIRD PARTY ON THE SERVICES, INCLUDING WITHOUT LIMITATION, ANY
        DEFAMATORY, OFFENSIVE OR ILLEGAL CONDUCT OF OTHER USERS OR THIRD PARTIES; (iii) ANY
        CONTENT OBTAINED FROM THE SERVICES; OR (iv) UNAUTHORIZED ACCESS, USE OR ALTERATION OF YOUR
        TRANSMISSIONS OR CONTENT.
        {'\n'}
        IN NO EVENT SHALL THE AGGREGATE LIABILITY OF THE HOST ENTITIES EXCEED THE GREATER OF ONE
        TWENTY FIVE U.S. DOLLARS (U.S. $25.00) OR THE AMOUNT YOU PAID US, IF ANY, IN THE PAST SIX
        MONTHS FOR THE SERVICES GIVING RISE TO THE CLAIM.
        {'\n'}
        THE LIMITATIONS OF THIS SUBSECTION SHALL APPLY TO ANY THEORY OF LIABILITY, WHETHER BASED
        ON WARRANTY, CONTRACT, STATUTE, TORT (INCLUDING NEGLIGENCE) OR OTHERWISE, AND WHETHER OR
        NOT THE HOST ENTITIES HAVE BEEN INFORMED OF THE POSSIBILITY OF ANY SUCH DAMAGE, AND EVEN
        IF A REMEDY SET FORTH HEREIN IS FOUND TO HAVE FAILED OF ITS ESSENTIAL PURPOSE.
      </div>

      <CloseBtn onClose={onClose} />
    </ModalShell>
  );
};

// Avatar — tiny silhouette using SVG, color depending on gender (pink=female, blue=male, black=other)
const Avatar = ({ gender }) => {
  const fill =
    gender === 'female' ? '#e85a92' :
    gender === 'male' ? '#3a7ad6' :
    '#222222';
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" style={{ verticalAlign: 'middle', marginRight: 4 }}>
      <circle cx="12" cy="8" r="4" fill={fill} stroke="#1c3a1c" strokeWidth="0.8" />
      <path d="M3 22c0-5 4-9 9-9s9 4 9 9z" fill={fill} stroke="#1c3a1c" strokeWidth="0.8" />
    </svg>
  );
};

// Role icon — Msg #978 refresh.
//  admin     → 🌟 (3D gold star) + ⚧ (genderless mark)
//  mod       → ♦ blue diamond gem (SVG, unchanged)
//  champion  → 🏆 gold trophy bar
// Champion rank is derived from presence-enriched `u.rank === 'Champion'`.
const RoleStar = ({ role }) => {
  if (role === 'admin') {
    return (
      <span
        style={{ verticalAlign: 'middle', marginRight: 3, fontSize: 13, lineHeight: 1 }}
        aria-label="Administrator"
        title="Administrator"
        data-testid="userlist-role-admin"
      >
        <span style={{ marginRight: 2, filter: 'drop-shadow(0 0 2px #e0b800)' }}>🌟</span>
        <span
          data-testid="userlist-role-admin-genderless"
          title="Genderless"
          style={{ color: '#5a3a8a', fontWeight: 'bold' }}
        >⚧</span>
      </span>
    );
  }
  if (role === 'champion') {
    return (
      <span
        data-testid="userlist-role-champion"
        aria-label="Champion"
        title="Champion (Adventurer rank)"
        style={{
          verticalAlign: 'middle', marginRight: 3, fontSize: 13, lineHeight: 1,
          filter: 'drop-shadow(0 0 2px #c9a13b)',
        }}
      >🏆</span>
    );
  }
  if (role === 'mod') {
    // Moderator diamond gem (bright cyan-blue, faceted look)
    return (
      <svg
        width="13" height="13" viewBox="0 0 24 24"
        style={{ verticalAlign: 'middle', marginRight: 3 }}
        aria-label="Moderator"
        data-testid="userlist-role-mod"
      >
        <path d="M6 3 h12 l4 6 l-10 12 l-10 -12 z" fill="#5cc3ff" stroke="#0d5a8a" strokeWidth="0.9" />
        <path d="M6 3 l4 6 l-8 0 z" fill="#a5deff" stroke="#0d5a8a" strokeWidth="0.5" />
        <path d="M18 3 l-4 6 l8 0 z" fill="#89d0f5" stroke="#0d5a8a" strokeWidth="0.5" />
        <path d="M10 9 l2 -6 l2 6 z" fill="#c3ecff" stroke="#0d5a8a" strokeWidth="0.5" />
        <path d="M2 9 l10 12 l0 -12 z" fill="#2a9adf" stroke="#0d5a8a" strokeWidth="0.5" />
      </svg>
    );
  }
  return null;
};

// Determine role for a given user. Admins match the small known set
// (mirrors backend ADMIN_USERS). Moderators may be passed in via the user object
// or through presence enrichment (u.is_moderator / u.role). Champions are
// derived from presence enrichment (u.rank === 'Champion').
const roleFor = (user) => {
  if (!user || !user.name) return null;
  const base = String(user.name).split('(')[0].trim().toLowerCase();
  if (user.is_admin || base === 'reccult') return 'admin';
  if (user.role === 'mod' || user.isModerator || user.is_moderator) return 'mod';
  if (String(user.rank || '').toLowerCase() === 'champion') return 'champion';
  return null;
};

// Availability dot — green = available/online, red = unavailable/offline.
const AvailabilityDot = ({ available, rp, title }) => {
  // Msg #987 — Blue orb wins over green/red when user is "Available for RP".
  let fill, stroke, label;
  if (rp) {
    fill = '#2563eb'; stroke = '#0f2a4a';
    label = 'available for RP';
  } else if (available) {
    fill = '#21a85a'; stroke = '#0c5a2b';
    label = 'available';
  } else {
    fill = '#c83434'; stroke = '#5a1010';
    label = 'unavailable';
  }
  return (
    <svg
      width="9" height="9" viewBox="0 0 12 12"
      style={{ verticalAlign: 'middle', marginRight: 4 }}
      aria-label={title || label}
    >
      <circle cx="6" cy="6" r="4.5" fill={fill} stroke={stroke} strokeWidth="1" />
    </svg>
  );
};

// Tiny crescent-moon "away" indicator shown beside a user's name when they are unavailable.
const AwayBadge = ({ awaySince }) => {
  // Compute a humanised "Last seen Xm ago" / "Xh ago" string based on awaySince ISO timestamp.
  const tooltip = React.useMemo(() => {
    if (!awaySince) return 'away — last seen just now';
    const t = Date.parse(awaySince);
    if (Number.isNaN(t)) return 'away';
    const diffMs = Date.now() - t;
    const mins = Math.max(1, Math.floor(diffMs / 60000));
    if (mins < 60) return `Last seen ${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    const rem = mins % 60;
    return rem === 0 ? `Last seen ${hrs}h ago` : `Last seen ${hrs}h ${rem}m ago`;
  }, [awaySince]);
  return (
    <span
      title={tooltip}
      data-testid="away-badge"
      data-away-since={awaySince || ''}
      style={{
        display: 'inline-block', marginLeft: 4, fontSize: 10,
        color: '#6a6a6a', verticalAlign: 'middle', cursor: 'help',
      }}
    >
      ☾ away
    </span>
  );
};

// ───────── Room Directory / User-List panel ─────────
// Shows every room with a (count) and the avatar+nick of users in each.
export const RoomDirectory = ({ rooms, currentRoom, presence, npcs, arousedNpcs = [], speakingSet = new Set(), onSwitchRoom, onViewUser, onRefresh, actionsBar, onBlockUser, onUnblockUser, onMessageUser, blockedUsers, isAdmin, voiceRoomMode = null, voiceOpen = false, onToggleVoice }) => {
  // Rooms the user has explicitly collapsed (so non-empty rooms don't auto-expand them again)
  const [collapsed, setCollapsed] = React.useState(() => new Set());
  // Rooms the user has explicitly expanded (overrides the "empty → collapsed" default)
  const [expanded, setExpanded] = React.useState(() => new Set([currentRoom]));
  // When the current room changes, make sure it's expanded and un-collapsed
  React.useEffect(() => {
    if (!currentRoom) return;
    setExpanded((prev) => new Set([...prev, currentRoom]));
    setCollapsed((prev) => {
      if (!prev.has(currentRoom)) return prev;
      const next = new Set(prev);
      next.delete(currentRoom);
      return next;
    });
  }, [currentRoom]);
  // Order rooms so the current room is always first
  const orderedRooms = React.useMemo(() => {
    if (!rooms || !currentRoom) return rooms || [];
    const rest = rooms.filter((r) => r !== currentRoom);
    return rooms.includes(currentRoom) ? [currentRoom, ...rest] : rooms;
  }, [rooms, currentRoom]);
  const [spinning, setSpinning] = React.useState(false);

  React.useEffect(() => {
    setExpanded((prev) => new Set([...prev, currentRoom]));
  }, [currentRoom]);

  const toggle = (r) => {
    // Manual toggle: flip between explicit-expand and explicit-collapse.
    const isCurrentlyOpen = expanded.has(r) || ((presence[r] || []).length + (npcs[r] || []).length > 0 && !collapsed.has(r));
    if (isCurrentlyOpen) {
      setExpanded((prev) => { const n = new Set(prev); n.delete(r); return n; });
      setCollapsed((prev) => new Set([...prev, r]));
    } else {
      setCollapsed((prev) => { const n = new Set(prev); n.delete(r); return n; });
      setExpanded((prev) => new Set([...prev, r]));
    }
  };

  const handleRefresh = () => {
    setSpinning(true);
    if (onRefresh) onRefresh();
    setTimeout(() => setSpinning(false), 450);
  };

  return (
    <div className="chat-userlist" style={{ padding: 8 }}>
      {actionsBar && (
        <>
          <div
            style={{
              textAlign: 'center',
              fontSize: 11,
              padding: '2px 0 6px 0',
              borderBottom: '1px solid #5b8c47',
              marginBottom: 6,
            }}
          >
            {actionsBar}
          </div>
        </>
      )}
      <div
        style={{
          textAlign: 'center',
          fontWeight: 'bold',
          textDecoration: 'underline',
          cursor: 'pointer',
          marginBottom: 6,
          color: spinning ? '#1a4a10' : '#1c3f1c',
          letterSpacing: spinning ? 1 : 0,
          transition: 'color 200ms, letter-spacing 200ms',
        }}
        onClick={handleRefresh}
        title="Click to refresh the user list"
      >
        {spinning ? '↻ REFRESHING...' : 'CHATROOM LIST'}
      </div>

      {orderedRooms.map((room) => {
        const users = presence[room] || [];
        const roomNpcs = npcs[room] || [];
        const total = users.length + roomNpcs.length;
        const isCurrent = room === currentRoom;
        const isOpen = expanded.has(room) || (!collapsed.has(room) && total > 0);

        return (
          <div key={room} style={{ marginBottom: 2 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                fontWeight: isCurrent || total > 0 ? 'bold' : 'normal',
                color: '#1c3f1c',
              }}
            >
              <span
                data-testid={`room-toggle-${room}`}
                onClick={() => toggle(room)}
                style={{
                  display: 'inline-block',
                  width: 14,
                  height: 14,
                  border: '1px solid #2d6b1e',
                  textAlign: 'center',
                  lineHeight: '12px',
                  fontSize: 11,
                  marginRight: 4,
                  fontFamily: 'monospace',
                  background: '#f0fae6',
                }}
              >
                {isOpen ? '−' : '+'}
              </span>
              <span
                onClick={() => onSwitchRoom && onSwitchRoom(room)}
                style={{
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  color: isCurrent ? '#1a4a10' : '#2d6b1e',
                }}
                title={`Go to ${room}`}
              >
                {room}
              </span>
              <span style={{ marginLeft: 4, fontSize: 11 }}>({total})</span>
              {/* Msg #1004 — Admin peek pill: click to warp into any private
                  room in cloaked (invisible) mode for moderation. Shown only
                  to admins on rooms the caller isn't already in. */}
              {isAdmin && String(room).startsWith('private_') && !isCurrent && (
                <span
                  data-testid={`admin-peek-${room}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSwitchRoom && onSwitchRoom(room, { peek: true });
                  }}
                  title={`Peek into ${room} invisibly (auto-cloaked)`}
                  style={{
                    marginLeft: 6, fontSize: 9, padding: '1px 5px',
                    borderRadius: 6, cursor: 'pointer', fontWeight: 'bold',
                    background: '#1c3a3a', color: '#dff5f5',
                    border: '1px solid #0e2222',
                    letterSpacing: 1, textTransform: 'uppercase',
                  }}
                >
                  🥷 PEEK
                </span>
              )}
            </div>
            {isOpen && total > 0 && (
              <div style={{ marginLeft: 18, marginTop: 2, marginBottom: 4 }}>
                {users.map((u) => {
                  const isAvail = u.available !== false;
                  const isRp = !!u.rp;  // Msg #987 — Available for RP
                  const role = roleFor(u);
                  const hideAvatar = role === 'admin' || role === 'mod';
                  return (
                    <div
                      key={`user-${u.name}`}
                      style={{
                        fontSize: 12, lineHeight: '16px',
                        display: 'flex', alignItems: 'center',
                        gap: 4, flexWrap: 'nowrap',
                        whiteSpace: 'nowrap',
                      }}
                      data-testid={`userlist-user-${u.name}`}
                    >
                      <AvailabilityDot
                        available={isAvail}
                        rp={isRp}
                        title={isRp ? 'available for RP' : (isAvail ? 'available' : 'away')}
                      />
                      <RoleStar role={role} />
                      {!hideAvatar && <Avatar gender={u.gender} />}
                      <span
                        style={{
                          color: u.color || '#1c3f1c',
                          cursor: 'pointer',
                          textDecoration: (blockedUsers && blockedUsers.has(String(u.name).toLowerCase()))
                            ? 'line-through'
                            : 'underline dotted',
                          fontWeight: u.isSelf ? 'bold' : 'normal',
                          opacity: isAvail ? 1 : 0.65,
                        }}
                        onClick={() => {
                          // Msg #785 revised — Clicking a user name in the
                          // userlist opens their PROFILE. Use the 💬 pill on
                          // the right to open Messenger.
                          if (onViewUser) onViewUser(u);
                        }}
                        title={u.isSelf ? `View your profile` : `View ${u.name}'s profile`}
                      >
                        {u.name}
                      </span>
                      {/* Msg #987 — "available for RP" inline label. */}
                      {isRp && (
                        <span
                          data-testid={`userlist-rp-label-${u.name}`}
                          title="This user is advertising as Available for RP"
                          style={{
                            marginLeft: 3,
                            fontSize: 9, fontWeight: 'bold',
                            color: '#ffffff',
                            background: '#2563eb',
                            padding: '0 5px',
                            borderRadius: 8,
                            letterSpacing: 1,
                            textTransform: 'uppercase',
                          }}
                        >available for RP</span>
                      )}
                      {/* Msg #982 — Live speaking indicator (⚡ mic pulse) when
                          this user is currently transmitting audio in voice. */}
                      {speakingSet && speakingSet.has(u.name) && (
                        <span
                          data-testid={`userlist-speaking-${u.name}`}
                          aria-label={`${u.name} is speaking`}
                          title={`${u.name} is speaking in voice`}
                          style={{
                            fontSize: 12, color: '#2d8a2d',
                            marginLeft: 2,
                            animation: 'mandala-mic-pulse 0.9s ease-in-out infinite',
                            filter: 'drop-shadow(0 0 3px #2d8a2d80)',
                          }}
                        >🎙</span>
                      )}
                      {u.isSelf && (
                        <span style={{ fontSize: 10, color: '#3a5a3a' }}>(you)</span>
                      )}
                      {!u.isSelf && onViewUser && (
                        <span
                          data-testid={`view-user-${u.name}`}
                          onClick={(e) => { e.stopPropagation(); onViewUser(u); }}
                          title={`View ${u.name}'s profile`}
                          style={{
                            cursor: 'pointer', fontSize: 11, marginLeft: 2,
                            color: '#2d6b1e', padding: '0 4px',
                            border: '1px solid #5b8c47', borderRadius: 4,
                            lineHeight: '14px', background: '#f0fae6',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#cce0b0')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = '#f0fae6')}
                        >
                          ?
                        </span>
                      )}
                      {!isAvail && <AwayBadge awaySince={u.awaySince} />}
                      {/* Msg #993 — START VOICE pill next to each user row in
                          voice-eligible rooms. Only shown for the CURRENT room
                          (voice is room-scoped). Toggles the LiveKit panel. */}
                      {voiceRoomMode && room === currentRoom && onToggleVoice && (
                        <span
                          data-testid={`userlist-voice-toggle-${u.name}`}
                          onClick={(e) => { e.stopPropagation(); onToggleVoice(); }}
                          title={voiceOpen
                            ? 'Leave voice/video'
                            : (voiceRoomMode === 'screenshare'
                                ? `Start screenshare in ${currentRoom}`
                                : `Start voice/video in ${currentRoom}`)}
                          style={{
                            cursor: 'pointer', fontSize: 9, marginLeft: 2,
                            padding: '1px 5px',
                            border: `1px solid ${voiceOpen ? '#7a1a1a' : '#0a3a0a'}`,
                            borderRadius: 8,
                            background: voiceOpen ? '#8b1a1a' : '#2d6b1e',
                            color: '#f5f5e8',
                            fontWeight: 'bold',
                            letterSpacing: 1,
                            textTransform: 'uppercase',
                          }}
                        >
                          {voiceOpen
                            ? '🚪 Leave'
                            : (voiceRoomMode === 'screenshare' ? '🖥 Share' : '🎙 Start Voice')}
                        </span>
                      )}
                      {!u.isSelf && onMessageUser && (
                        <span
                          data-testid={`message-user-${u.name}`}
                          onClick={(e) => { e.stopPropagation(); onMessageUser(u); }}
                          title={`Message ${u.name}`}
                          style={{
                            cursor: 'pointer',
                            fontSize: 12,
                            color: '#2d6b1e',
                            marginLeft: 2,
                            padding: '0 4px',
                            border: '1px solid #5b8c47',
                            borderRadius: 4,
                            lineHeight: '14px',
                            background: '#f0fae6',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#cce0b0')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = '#f0fae6')}
                        >
                          💬
                        </span>
                      )}
                      {!u.isSelf && (() => {
                        const key = String(u.name).toLowerCase();
                        const isBlocked = blockedUsers && blockedUsers.has(key);
                        if (isBlocked && onUnblockUser) {
                          return (
                            <span
                              data-testid={`unblock-user-${u.name}`}
                              onClick={(e) => { e.stopPropagation(); onUnblockUser(u); }}
                              title={`Un-ignore ${u.name}`}
                              style={{ cursor: 'pointer', fontSize: 11, color: '#1a6a10', marginLeft: 2, fontWeight: 'bold' }}
                            >↻</span>
                          );
                        }
                        if (!isBlocked && onBlockUser) {
                          return (
                            <span
                              data-testid={`block-user-${u.name}`}
                              onClick={(e) => { e.stopPropagation(); onBlockUser(u); }}
                              title={`Ignore/Block ${u.name}`}
                              style={{ cursor: 'pointer', fontSize: 10, color: '#a02020', marginLeft: 2, fontWeight: 'bold' }}
                            >⊘</span>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  );
                })}
                {roomNpcs.map((npc) => {
                  // Msg #979 — ⚡ arousal indicator: if a live /arouse bias
                  // exists for THIS viewer against THIS NPC, pulse a red heart
                  // spark next to the NPC's name.
                  const isAroused = Array.isArray(arousedNpcs) && arousedNpcs.includes(npc.name);
                  return (
                    <div
                      key={`npc-${npc.name}`}
                      style={{
                        fontSize: 12, lineHeight: '16px',
                        display: 'flex', alignItems: 'center',
                        gap: 4, flexWrap: 'nowrap', whiteSpace: 'nowrap',
                        cursor: onViewUser ? 'pointer' : 'default',
                      }}
                      data-testid={`userlist-npc-${npc.name}`}
                      title={`View ${npc.name}'s profile`}
                      onClick={() => onViewUser && onViewUser(npc)}
                    >
                      <AvailabilityDot available title="available now" />
                      <Avatar gender={npc.gender} />
                      <span
                        style={{
                          color: npc.color,
                          textDecoration: 'underline dotted',
                          flex: 1,
                        }}
                      >
                        {npc.name}
                      </span>
                      {isAroused && (
                        <span
                          data-testid={`userlist-npc-aroused-${npc.name}`}
                          aria-label={`${npc.name} is aroused by your presence`}
                          title={`${npc.name} is drawn to you — /arouse bias is live`}
                          style={{
                            fontSize: 12, color: '#c1272d',
                            animation: 'mandala-heart-pulse 1.4s ease-in-out infinite',
                            marginRight: 2, filter: 'drop-shadow(0 0 3px #c1272d80)',
                          }}
                        >⚡</span>
                      )}
                      <span
                        data-testid={`userlist-npc-tag-${npc.name}`}
                        style={{ fontSize: 10, color: '#3a5a3a' }}
                      >
                        {/* Msg #1003 — Show the NPC's real category: (NPC),
                            (Monster), (Pokemon), (Pokemorph), or (Champion).
                            Falls back based on isMonster for legacy rows. */}
                        ({(() => {
                          const t = (npc.npcType || '').toUpperCase();
                          if (t === 'POKEMON')   return 'POKEMON';
                          if (t === 'POKEMORPH') return 'POKEMORPH';
                          if (t === 'CHAMPION')  return 'CHAMPION';
                          if (t === 'MONSTER' || npc.isMonster) return 'MONSTER';
                          return 'NPC';
                        })()})
                      </span>
                      {onViewUser && (
                        <span
                          data-testid={`view-npc-${npc.name}`}
                          onClick={(e) => { e.stopPropagation(); onViewUser(npc); }}
                          title={`View ${npc.name}'s profile`}
                          style={{
                            cursor: 'pointer', fontSize: 11, marginLeft: 2,
                            color: '#2d6b1e', padding: '0 4px',
                            border: '1px solid #5b8c47', borderRadius: 4,
                            lineHeight: '14px', background: '#f0fae6',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#cce0b0')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = '#f0fae6')}
                        >
                          ?
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};


// ───────── Messenger Box (PM popup) ─────────
// A small floating box that holds a list of conversations on the left and the
// selected thread on the right. Minimizable / closable. Sender drives sending
// via the onSendWhisper callback.
export const MessengerBox = ({
  open,
  minimized,
  whispers,
  myUserId,
  myName,
  unreadCount,
  onSendWhisper,
  onMinimize,
  onClose,
  onMarkRead,
  closedConvs,
  onCloseConversation,
  onReopenConversation,
  onBlockUser,
  requestedActiveKey,
  onActiveKeyConsumed,
  friends,
  blockedUsers,
  onlineUsers,
  onAddFriend,
  onRemoveFriend,
  onOpenProfile,
  typingBySender,        // Msg #791 — { name_lc: true }
  onTypingHeartbeat,     // Msg #791 — throttled parent-side heartbeat
}) => {
  const [activeKey, setActiveKey] = React.useState(null);
  const [composeTo, setComposeTo] = React.useState('');
  const [draft, setDraft] = React.useState('');
  const threadRef = React.useRef(null);

  // Build conversations grouped by counterparty
  const conversations = React.useMemo(() => {
    const map = {};
    const closed = closedConvs || new Set();
    (whispers || []).forEach((w) => {
      // Msg #989 — `/whisper` and `/to` (via='cmd') are chatroom-scoped and
      // MUST NOT appear as a Messenger conversation. Messenger only shows
      // PM-style whispers (via='pm') and offline PMs.
      if ((w.via || 'cmd') === 'cmd') return;
      const isFromMe = w.from_user_id === myUserId;
      const otherName = isFromMe ? w.to_name : w.from_name;
      const otherUserId = isFromMe ? w.to_user_id : w.from_user_id;
      const otherKey = String(otherName || '').toLowerCase();
      if (!otherKey || closed.has(otherKey)) return;
      if (!map[otherKey]) {
        map[otherKey] = {
          key: otherKey, name: otherName, color: w.from_color || '#5a3a8a',
          otherUserId, messages: [], lastTs: w.ts, unread: 0,
        };
      }
      map[otherKey].messages.push({
        id: w.id,
        from: isFromMe ? myName : (w.from_name || otherName),
        text: w.text,
        ts: w.ts,
        mine: isFromMe,
        isNpc: !!w.is_npc,
        readAt: w.read_at || null,  // Msg #786 — WhatsApp-style ✓✓
        isShadow: !!w.is_shadow,   // Msg #986 — red-themed admin Shadow Whisper
      });
      map[otherKey].lastTs = w.ts;
      // Msg #786 — Whispers received by me that have no read_at are unread.
      if (!isFromMe && !w.read_at) map[otherKey].unread += 1;
    });
    return Object.values(map).sort((a, b) => (a.lastTs < b.lastTs ? 1 : -1));
  }, [whispers, myUserId, myName, closedConvs]);

  // Msg #796 — Only auto-scroll to bottom on:
  //   (1) initial open of the conversation (activeKey/open change), or
  //   (2) a new message arriving *while* the user was already pinned to the
  //       bottom (within 32px).  If they've scrolled up to read history we
  //       leave them alone.
  const lastActiveKeyRef = React.useRef(null);
  const wasAtBottomRef = React.useRef(true);
  React.useEffect(() => {
    if (!open || minimized || !activeKey || !threadRef.current) return;
    const el = threadRef.current;
    const activeChanged = lastActiveKeyRef.current !== activeKey;
    lastActiveKeyRef.current = activeKey;
    if (activeChanged) {
      // Force scroll to the newest message when the user selects a tab.
      el.scrollTop = el.scrollHeight;
      wasAtBottomRef.current = true;
      return;
    }
    // Otherwise only follow-the-tail if the user was already at (or near) the bottom.
    if (wasAtBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [activeKey, conversations, open, minimized]);

  // Track scroll position so subsequent poll-updates know whether to auto-follow.
  const onThreadScroll = React.useCallback(() => {
    const el = threadRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    wasAtBottomRef.current = dist < 32;
  }, []);

  // Msg #786 — Passing the *display name* (not the lowercased key) to onMarkRead
  // so the backend's case-sensitive query matches the whisper docs.
  React.useEffect(() => {
    if (open && !minimized && activeKey && onMarkRead) {
      const conv = conversations.find((c) => c.key === activeKey);
      if (conv && conv.unread > 0) {
        onMarkRead(conv.name, conv.otherUserId);
      }
    }
  }, [open, minimized, activeKey, onMarkRead, conversations]);

  // Allow a parent (e.g. ViewProfileModal "Continue this scene" button) to request
  // a particular conversation be selected when the box opens.
  React.useEffect(() => {
    if (!requestedActiveKey) return;
    // Msg #786 — Auto-reopen if this conversation had been closed.
    if (onReopenConversation) onReopenConversation(requestedActiveKey);
    setActiveKey(requestedActiveKey);
    // Msg #791 — If no whisper history exists yet with this counterparty,
    // pre-fill the compose "Message to:" field with their name.  We ALWAYS
    // overwrite here so switching between fresh counterparties works.
    const already = (whispers || []).some((w) => {
      const other = (w.from_user_id === myUserId ? w.to_name : w.from_name) || '';
      return String(other).toLowerCase() === requestedActiveKey;
    });
    if (!already) {
      const friend = (friends || []).find((f) => String(f.name || '').toLowerCase() === requestedActiveKey);
      setComposeTo(friend ? friend.name : requestedActiveKey);
    }
    if (onActiveKeyConsumed) onActiveKeyConsumed();
  }, [requestedActiveKey, onActiveKeyConsumed, onReopenConversation, whispers, myUserId, friends]);

  // Msg #774 — Draggable position, persisted to localStorage. `null` means
  // "default anchor to bottom-right".  Positions are stored as {x, y} in
  // absolute page coords (left/top).
  const [pos, setPos] = React.useState(() => {
    try {
      const raw = localStorage.getItem('mandala_messenger_pos');
      return raw ? JSON.parse(raw) : null;
    } catch (e) { void e; return null; }
  });
  const dragStateRef = React.useRef(null);

  React.useEffect(() => {
    const onMove = (ev) => {
      const st = dragStateRef.current;
      if (!st) return;
      const x = Math.max(0, Math.min(window.innerWidth - 100, ev.clientX - st.dx));
      const y = Math.max(0, Math.min(window.innerHeight - 40, ev.clientY - st.dy));
      setPos({ x, y });
    };
    const onUp = () => {
      if (dragStateRef.current) {
        try { localStorage.setItem('mandala_messenger_pos', JSON.stringify(pos)); }
        catch (e) { void e; }
      }
      dragStateRef.current = null;
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [pos]);

  const startDrag = (ev) => {
    // Ignore drags starting on the minimize/close pips.
    if (ev.target && ev.target.dataset && ev.target.dataset.nodrag) return;
    ev.preventDefault();
    const rect = ev.currentTarget.parentElement.getBoundingClientRect();
    dragStateRef.current = { dx: ev.clientX - rect.left, dy: ev.clientY - rect.top };
    document.body.style.userSelect = 'none';
  };

  // Msg #985 — Bug-fix state for async send flow.
  const [sending, setSending] = React.useState(false);
  const [sendError, setSendError] = React.useState('');

  if (!open) return null;

  const active = conversations.find((c) => c.key === activeKey);

  const handleSend = async () => {
    // Msg #985 — Bug: messages "randomly" disappeared when sendWhisper
    // resolved asynchronously (draft was cleared before the network round-trip
    // completed, and failures were swallowed silently). Fix:
    //   1. await the send
    //   2. clear the draft ONLY on success
    //   3. surface network failures as an inline error under the input
    //   4. re-enable the input immediately on failure so the user can retry
    if (sending) return;   // guard double-tap Enter
    const text = draft.trim();
    const to = (active ? active.name : composeTo).trim();
    // Msg #985 fix — replace the previous silent early-return with an
    // explicit inline error so users learn what to type.
    if (!to) { setSendError('Type a recipient username first.'); return; }
    if (!text) { setSendError('Type a message first.'); return; }
    setSending(true);
    setSendError('');
    try {
      const result = onSendWhisper(to, text);
      // Support both sync and async onSendWhisper implementations.
      if (result && typeof result.then === 'function') await result;
      setDraft('');
      if (!active) {
        setActiveKey(to.toLowerCase());
        setComposeTo('');
      }
    } catch (e) {
      // Msg #991 — Messenger PM errors ALWAYS use "message" wording (never
      // "whisper" — those are separate features). If the backend passes
      // through a "whisper" error string we rewrite it.
      const status = e && e.response && e.response.status;
      const detail = e && e.response && e.response.data && e.response.data.detail;
      let msg;
      if (status === 429) {
        msg = 'Server is rate-limiting messages right now. Wait a few seconds and try again.';
      } else if (detail) {
        msg = String(detail).replace(/whisper/gi, 'message');
      } else {
        msg = `Could not send message to ${to}.`;
      }
      setSendError(msg);
    } finally {
      setSending(false);
    }
  };

  // ── Minimized pill in the bottom-right ──
  if (minimized) {
    return (
      <div
        data-testid="messenger-minimized-bar"
        onClick={onMinimize}
        className="room-themed-header"
        style={{
          position: 'fixed',
          bottom: 12,
          right: 16,
          border: '1px solid',
          padding: '6px 14px',
          fontSize: 12,
          fontWeight: 'bold',
          cursor: 'pointer',
          zIndex: 900,
          boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
          letterSpacing: 1,
          fontFamily: 'Arial, Helvetica, sans-serif',
        }}
      >
        PM {unreadCount > 0 ? `(${unreadCount} new)` : ''} ▴
      </div>
    );
  }

  return (
    <div
      data-testid="messenger-box"
      className="room-themed-panel"
      style={{
        position: 'fixed',
        ...(pos
          ? { left: pos.x, top: pos.y }
          : { bottom: 12, right: 16 }),
        width: 480,
        maxWidth: '94vw',
        height: 360,
        maxHeight: '70vh',
        border: '2px solid',
        boxShadow: '0 8px 28px rgba(0,0,0,0.45)',
        zIndex: 900,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Arial, Helvetica, sans-serif',
      }}
    >
      {/* Header (drag handle) */}
      <div
        data-testid="messenger-drag-handle"
        onMouseDown={startDrag}
        className="room-themed-header"
        style={{
          padding: '4px 10px',
          fontSize: 12,
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          letterSpacing: 1,
          cursor: 'move',
          userSelect: 'none',
        }}
      >
        <span style={{ flex: 1 }}>MESSENGER</span>
        <span
          data-testid="messenger-reset-pos"
          data-nodrag="1"
          title="Reset position"
          onClick={(e) => {
            e.stopPropagation();
            setPos(null);
            try { localStorage.removeItem('mandala_messenger_pos'); } catch (err) { void err; }
          }}
          style={{ cursor: 'pointer', padding: '0 6px', fontSize: 10 }}
        >
          ⟲
        </span>
        <span
          data-testid="messenger-minimize"
          data-nodrag="1"
          title="Minimize"
          onClick={onMinimize}
          style={{ cursor: 'pointer', padding: '0 8px' }}
        >
          ▾
        </span>
        <span
          data-testid="messenger-close"
          data-nodrag="1"
          title="Close"
          onClick={onClose}
          style={{ cursor: 'pointer', padding: '0 4px' }}
        >
          ✕
        </span>
      </div>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Conversation list */}
        <div
          style={{
            width: 160,
            borderRight: '1px solid #5b8c47',
            overflowY: 'auto',
            background: '#e9f3da',
          }}
        >
          <div
            onClick={() => { setActiveKey(null); setComposeTo(''); }}
            style={{
              padding: '6px 8px',
              fontSize: 11,
              fontWeight: 'bold',
              borderBottom: '1px solid #5b8c47',
              cursor: 'pointer',
              background: !active ? '#cce0b0' : 'transparent',
              color: '#1a4a10',
            }}
            data-testid="messenger-new-thread"
          >
            + New message…
          </div>
          {conversations.length === 0 && (
            <div style={{ padding: 10, fontSize: 11, color: '#5a5a5a', fontStyle: 'italic' }}>
              No whispers yet.
            </div>
          )}
          {conversations.map((c) => (
            <div
              key={c.key}
              data-testid={`messenger-conv-${c.name}`}
              onClick={() => setActiveKey(c.key)}
              style={{
                padding: '6px 8px',
                fontSize: 12,
                cursor: 'pointer',
                borderBottom: '1px solid #b6cf9c',
                background: activeKey === c.key ? '#cce0b0' : 'transparent',
                fontWeight: c.unread > 0 ? 'bold' : 'normal',
                display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              <span
                style={{
                  color: c.color, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
                  cursor: 'pointer',
                  textDecoration: 'underline dotted',
                }}
                data-testid={`messenger-conv-name-${c.name}`}
                onClick={(e) => {
                  // Msg #785 revised — Clicking a conversation name jumps to
                  // the last message with that user (opens the thread + auto-
                  // scrolls to bottom via the activeKey effect).
                  e.stopPropagation();
                  if (onReopenConversation) onReopenConversation(c.key);
                  setActiveKey(c.key);
                }}
                title={`Open conversation with ${c.name}`}
              >
                {c.name}
              </span>
              {c.unread > 0 && (
                <span style={{ fontSize: 10, color: '#a02020' }}>({c.unread})</span>
              )}
              {typingBySender && typingBySender[c.key] && (
                <span
                  data-testid={`messenger-typing-${c.name}`}
                  title={`${c.name} is typing…`}
                  style={{
                    fontSize: 10, color: '#1a4a10',
                    fontStyle: 'italic', marginLeft: 4,
                    animation: 'pulse 1.2s ease-in-out infinite',
                  }}
                >
                  typing…
                </span>
              )}
              {onBlockUser && (
                <span
                  data-testid={`messenger-block-${c.name}`}
                  onClick={(e) => { e.stopPropagation(); onBlockUser({ name: c.name }); }}
                  title={`Block ${c.name}`}
                  style={{ fontSize: 11, color: '#a02020', cursor: 'pointer', padding: '0 2px' }}
                >
                  ⊘
                </span>
              )}
              <span
                data-testid={`messenger-close-conv-${c.name}`}
                onClick={(e) => { e.stopPropagation(); onCloseConversation && onCloseConversation(c.key); if (activeKey === c.key) setActiveKey(null); }}
                title="Close this conversation"
                style={{ fontSize: 12, color: '#5a3a3a', cursor: 'pointer', padding: '0 2px' }}
              >
                ✕
              </span>
            </div>
          ))}

          {/* Msg #993 — FRIENDS section extracted to standalone <FriendsListBox>. */}
        </div>

        {/* Thread / Compose */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {active ? (
            <>
              {/* Active-thread header — click the recipient's name to open their profile */}
              <div
                style={{
                  borderBottom: '1px solid #5b8c47',
                  background: '#e9f3da',
                  padding: '5px 10px',
                  fontSize: 12,
                  color: '#1c3f1c',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <span style={{ opacity: 0.7 }}>Whispering with</span>
                <b
                  data-testid={`messenger-active-name-${active.name}`}
                  onClick={() => onOpenProfile && onOpenProfile({ name: active.name, color: active.color, isNPC: !!active.isNpc })}
                  title={onOpenProfile ? `View ${active.name}'s profile` : undefined}
                  style={{
                    color: active.color || '#1c3f1c',
                    cursor: onOpenProfile ? 'pointer' : 'default',
                    textDecoration: onOpenProfile ? 'underline dotted' : 'none',
                  }}
                >
                  {active.name}
                </b>
              </div>
              <div
                ref={threadRef}
                onScroll={onThreadScroll}
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: '6px 10px',
                  fontSize: 12,
                  lineHeight: 1.5,
                }}
                data-testid="messenger-thread"
              >
              {active.messages.map((m) => (
                <div
                  key={m.id}
                  style={{
                    marginBottom: 4,
                    ...(m.isShadow ? {
                      background: 'rgba(160,20,20,0.12)',
                      borderLeft: '3px solid #a02020',
                      padding: '2px 6px',
                      borderRadius: 3,
                    } : {}),
                  }}
                  data-testid={m.isShadow ? `messenger-msg-shadow-${m.id}` : undefined}
                >
                  {m.isShadow && (
                    <span
                      style={{
                        display: 'inline-block',
                        marginRight: 6,
                        padding: '0 5px',
                        background: '#a02020', color: '#fff3e0',
                        border: '1px solid #7a1a1a',
                        fontSize: 9, fontWeight: 'bold',
                        letterSpacing: 1, textTransform: 'uppercase',
                        borderRadius: 8,
                      }}
                    >🥷 Shadow</span>
                  )}
                  <b
                    style={{
                      color: m.isShadow ? '#a02020' : (m.mine ? '#1a4a10' : (m.isNpc ? '#8B2C2C' : '#5a3a8a')),
                      cursor: onOpenProfile && !m.mine ? 'pointer' : 'default',
                      textDecoration: onOpenProfile && !m.mine ? 'underline dotted' : 'none',
                    }}
                    data-testid={`messenger-msg-sender-${m.from}`}
                    onClick={() => {
                      if (!onOpenProfile || m.mine) return;
                      onOpenProfile({
                        name: m.from,
                        color: m.isNpc ? '#8B2C2C' : '#5a3a8a',
                        isNPC: !!m.isNpc,
                      });
                    }}
                    title={onOpenProfile && !m.mine ? `View ${m.from}'s profile` : undefined}
                  >
                    {m.from}:
                  </b>{' '}
                  <span style={{
                    whiteSpace: 'pre-wrap',
                    color: m.isShadow ? '#7a0f14' : undefined,
                    fontWeight: m.isShadow ? 600 : undefined,
                  }}>{m.text}</span>
                  {/* Msg #786 — WhatsApp-style receipts on OUTGOING whispers. */}
                  {m.mine && (
                    <span
                      data-testid={`messenger-msg-receipt-${m.id}`}
                      title={m.readAt ? `Read ${new Date(m.readAt).toLocaleString()}` : 'Delivered'}
                      style={{
                        marginLeft: 6,
                        fontSize: 11,
                        color: m.readAt ? '#1e88ff' : '#5a7a3a',
                        fontWeight: 'bold',
                        letterSpacing: -1,
                      }}
                    >
                      {m.readAt ? '✓✓' : '✓'}
                    </span>
                  )}
                </div>
              ))}
              {active && typingBySender && typingBySender[active.key] && (
                <div
                  data-testid={`messenger-typing-active-${active.name}`}
                  style={{
                    marginTop: 6,
                    fontSize: 11,
                    fontStyle: 'italic',
                    color: '#1a4a10',
                    animation: 'pulse 1.2s ease-in-out infinite',
                  }}
                >
                  {active.name} is typing<span style={{ letterSpacing: 2 }}>…</span>
                </div>
              )}
            </div>
            </>
          ) : (
            <div style={{ padding: 10, fontSize: 12, color: '#1c3f1c' }}>
              <div style={{ marginBottom: 6, fontWeight: 'bold' }}>Message to:</div>
              <input
                value={composeTo}
                onChange={(e) => setComposeTo(e.target.value)}
                placeholder="username or NPC name"
                data-testid="messenger-new-to"
                style={{
                  background: '#1c3a1c',
                  color: '#d4f1c4',
                  border: '1px solid #2d6b1e',
                  padding: '3px 6px',
                  fontSize: 12,
                  width: '100%',
                }}
              />
              <div style={{ marginTop: 8, fontSize: 11, color: '#3a5a3a' }}>
                Whispers are private. NPCs in Strip_Club / Inn_Brothel will reply if online.
              </div>
              {onOpenProfile && composeTo && composeTo.trim().length >= 2 && (
                <div
                  data-testid="messenger-compose-view-profile"
                  onClick={() => onOpenProfile({ name: composeTo.trim() })}
                  style={{
                    marginTop: 6, fontSize: 11, color: '#1a4a10',
                    textDecoration: 'underline dotted', cursor: 'pointer',
                  }}
                  title={`View ${composeTo.trim()}'s profile`}
                >
                  → View {composeTo.trim()}&apos;s profile
                </div>
              )}
            </div>
          )}

          {/* Input */}
          <div
            style={{
              borderTop: '1px solid #5b8c47',
              padding: 6,
              display: 'flex',
              gap: 4,
              background: '#e9f3da',
            }}
          >
            <input
              data-testid="messenger-input"
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                // Msg #791 — throttled typing heartbeat to the current
                // conversation counterparty.
                if (active && onTypingHeartbeat && e.target.value.length > 0) {
                  onTypingHeartbeat(active.name, active.otherUserId);
                }
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder={active ? `Message to ${active.name}…` : 'Type your message…'}
              style={{
                flex: 1,
                background: '#1c3a1c',
                color: '#d4f1c4',
                border: '1px solid #2d6b1e',
                padding: '3px 6px',
                fontSize: 12,
              }}
            />
            <button
              data-testid="messenger-send"
              onClick={handleSend}
              disabled={sending}
              style={{
                background: sending ? '#3a3a2a' : '#1c3a1c',
                color: '#ecfcd9',
                border: '1px solid #2d6b1e',
                padding: '2px 12px',
                fontSize: 11,
                fontWeight: 'bold',
                cursor: sending ? 'not-allowed' : 'pointer',
                opacity: sending ? 0.7 : 1,
              }}
            >
              {sending ? '…' : 'SEND'}
            </button>
          </div>
          {sendError && (
            <div
              data-testid="messenger-send-error"
              style={{
                margin: '2px 4px 4px', padding: '2px 6px',
                background: '#3a1a1a', color: '#f9b6b6',
                border: '1px solid #8b2a2a', borderRadius: 4,
                fontSize: 11,
              }}
            >⚠ {sendError} — tap SEND again to retry.</div>
          )}
        </div>
      </div>
    </div>
  );
};


// ───────── Friends List Box (Msg #993) ─────────
// Standalone floating panel that holds the user's friends list. Extracted from
// MessengerBox so the Friends panel and the Messenger PM box are independent
// UX elements. Clicking a friend opens the Messenger and jumps to that
// conversation (via onOpenConversation({name})).
export const FriendsListBox = ({
  open,
  minimized,
  friends,
  onlineUsers,
  blockedUsers,
  onAddFriend,
  onRemoveFriend,
  onBlockUser,
  onOpenConversation,   // ({ name }) => void — opens Messenger + selects thread
  onMinimize,
  onClose,
}) => {
  if (!open) return null;

  const shell = {
    position: 'fixed',
    right: 12, bottom: minimized ? 12 : 150,
    width: 240, zIndex: 44,
    background: '#e9f3da',
    border: '2px solid #2d6b1e',
    boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
    fontFamily: 'Georgia, "Times New Roman", serif',
    color: '#1c3f1c',
    borderRadius: 6,
    overflow: 'hidden',
  };
  const header = {
    background: '#2d6b1e', color: '#f5f5e8',
    padding: '4px 10px', fontSize: 11, fontWeight: 'bold',
    letterSpacing: 2, textTransform: 'uppercase',
    display: 'flex', alignItems: 'center', gap: 6,
  };

  return (
    <div data-testid="friends-list-box" style={shell}>
      <div style={header}>
        <span style={{ flex: 1 }}>
          👥 Friends {friends && friends.length > 0 ? `(${friends.length})` : ''}
        </span>
        <span
          data-testid="friends-list-add"
          title="Add a friend by name"
          onClick={() => {
            const name = window.prompt('Friend username to add:');
            if (name && name.trim() && onAddFriend) onAddFriend(name.trim());
          }}
          style={{ cursor: 'pointer', padding: '0 4px', fontSize: 14 }}
        >+</span>
        {onMinimize && (
          <span
            data-testid="friends-list-min"
            onClick={onMinimize}
            title={minimized ? 'Expand' : 'Minimize'}
            style={{ cursor: 'pointer', padding: '0 4px' }}
          >{minimized ? '▲' : '▼'}</span>
        )}
        {onClose && (
          <span
            data-testid="friends-list-close"
            onClick={onClose}
            title="Close friends list"
            style={{ cursor: 'pointer', padding: '0 4px' }}
          >✕</span>
        )}
      </div>
      {!minimized && (
        <div style={{ maxHeight: 260, overflowY: 'auto' }} data-testid="friends-list-body">
          {(!friends || friends.length === 0) && (
            <div style={{ padding: '8px 12px', fontSize: 12, fontStyle: 'italic', color: '#3a5a3a' }}>
              No friends added yet. Click <b>+</b> above to add one.
            </div>
          )}
          {(friends || []).map((fname) => {
            const fkey = String(fname).toLowerCase();
            const isBlocked = blockedUsers && blockedUsers.has(fkey);
            const isOnline = onlineUsers && onlineUsers.has(fkey);
            return (
              <div
                key={`flb-${fname}`}
                data-testid={`friends-list-row-${fname}`}
                onClick={() => onOpenConversation && onOpenConversation({ name: fname })}
                style={{
                  padding: '6px 10px', fontSize: 12, cursor: 'pointer',
                  borderTop: '1px dotted #b6cf9c',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#cce0b0')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <span
                  data-testid={`friends-list-dot-${fname}`}
                  title={isOnline ? `${fname} is online` : `${fname} is offline`}
                  style={{
                    display: 'inline-block',
                    width: 8, height: 8, borderRadius: '50%',
                    background: isOnline ? '#1f9d3a' : '#8a8a8a',
                    border: '1px solid #1c3a1c',
                    flexShrink: 0,
                  }}
                />
                <span
                  data-testid={`friends-list-name-${fname}`}
                  style={{
                    flex: 1, color: '#1c3f1c',
                    textDecoration: isBlocked ? 'line-through' : 'underline dotted',
                    opacity: isBlocked ? 0.6 : 1,
                  }}
                  title={`Message ${fname}`}
                >
                  {fname}
                </span>
                {onBlockUser && (
                  <span
                    data-testid={`friends-list-block-${fname}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isBlocked) onBlockUser({ name: fname, _toggle: 'unblock' });
                      else onBlockUser({ name: fname });
                    }}
                    title={isBlocked ? `Un-ignore ${fname}` : `Ignore ${fname}`}
                    style={{
                      fontSize: 11, fontWeight: 'bold',
                      color: isBlocked ? '#1a6a10' : '#a02020',
                      cursor: 'pointer', padding: '0 2px',
                    }}
                  >
                    {isBlocked ? '↻' : '⊘'}
                  </span>
                )}
                {onRemoveFriend && (
                  <span
                    data-testid={`friends-list-remove-${fname}`}
                    onClick={(e) => { e.stopPropagation(); onRemoveFriend(fname); }}
                    title={`Remove ${fname} from friends`}
                    style={{ fontSize: 12, color: '#5a3a3a', cursor: 'pointer', padding: '0 2px' }}
                  >
                    ✕
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
